/** Seeded BillingPlan.code for canceled / terminal unpaid subscriptions. */
export const EXPIRED_BILLING_PLAN_CODE = 'expired' as const

/** Stable code for the system-default BillingCreditCostSet. */
export const DEFAULT_CREDIT_COST_SET_CODE = '_default' as const

/** @deprecated Use DEFAULT_CREDIT_COST_SET_CODE — kept for brief compatibility during rename. */
export const DEFAULT_CREDIT_COSTS_PLAN_CODE = DEFAULT_CREDIT_COST_SET_CODE

export const BILLING_PLAN_INTERVALS = ['month', 'year'] as const
export type BillingPlanInterval = (typeof BILLING_PLAN_INTERVALS)[number]

export const BILLING_CREDIT_GRANT_MODES = ['one_time', 'monthly', 'contract'] as const
export type BillingCreditGrantMode = (typeof BILLING_CREDIT_GRANT_MODES)[number]

/** @deprecated Use BILLING_CREDIT_PACK_SIZES */
export const BILLING_CAPACITY_BLOCK_SIZES = [3, 5, 10, 25, 50, 100, 250, 500, 1000] as const
/** Prepaid credit pack sizes (credits). Price = size × CREDIT_UNIT_AMOUNT_CENTS. */
export const BILLING_CREDIT_PACK_SIZES = BILLING_CAPACITY_BLOCK_SIZES
export type BillingCreditPackSize = (typeof BILLING_CREDIT_PACK_SIZES)[number]
/** @deprecated Use BillingCreditPackSize */
export type BillingCapacityBlockSize = BillingCreditPackSize

/** Seed / fallback list price per credit in cents ($0.10). Runtime value lives on BillingCreditCostSet.unitAmountCents. */
export const CREDIT_UNIT_AMOUNT_CENTS = 10 as const

export const FREE_INCLUDED_CREDITS = 100 as const
export const PRO_INCLUDED_CREDITS = 500 as const
export const PRO_MONTHLY_AMOUNT_CENTS = PRO_INCLUDED_CREDITS * CREDIT_UNIT_AMOUNT_CENTS

export const DEFAULT_ANNUAL_DISCOUNT_PERCENT = 20

export const BILLING_CREDIT_ACTION_CODES = [
    'resume_processing',
    'ai_interview',
    'phone_screening',
    'workshort',
    'human_interview_summary',
    'jd_creation',
    'marley_chat',
    'ai_analytics',
] as const
export type BillingCreditActionCode = (typeof BILLING_CREDIT_ACTION_CODES)[number]

export const BILLING_CREDIT_UNIT_KINDS = ['per_action', 'per_n_turns', 'per_query'] as const
export type BillingCreditUnitKind = (typeof BILLING_CREDIT_UNIT_KINDS)[number]

export type BillingCreditCostEntry = {
    title: string
    credits: number
    unitKind: BillingCreditUnitKind
    unitSize: number
}

export type BillingCreditCostsMap = Partial<Record<BillingCreditActionCode, BillingCreditCostEntry>>

/** Seed / TypeScript contract for the default BillingCreditCostSet.costs. DB is runtime source of truth. */
export const DEFAULT_BILLING_CREDIT_COSTS: Record<BillingCreditActionCode, BillingCreditCostEntry> = {
    resume_processing: {
        title: 'Resume / candidate processing',
        credits: 5,
        unitKind: 'per_action',
        unitSize: 1,
    },
    ai_interview: {
        title: 'AI interview',
        credits: 25,
        unitKind: 'per_action',
        unitSize: 1,
    },
    phone_screening: {
        title: 'Phone screening',
        credits: 15,
        unitKind: 'per_action',
        unitSize: 1,
    },
    workshort: {
        title: 'WorkShort',
        credits: 10,
        unitKind: 'per_action',
        unitSize: 1,
    },
    human_interview_summary: {
        title: 'Human interview AI summary',
        credits: 1,
        unitKind: 'per_action',
        unitSize: 1,
    },
    jd_creation: {
        title: 'JD creation',
        credits: 1,
        unitKind: 'per_action',
        unitSize: 1,
    },
    marley_chat: {
        title: 'Marley Chatbot',
        credits: 1,
        unitKind: 'per_n_turns',
        unitSize: 10,
    },
    ai_analytics: {
        title: 'AI Analytics',
        credits: 1,
        unitKind: 'per_query',
        unitSize: 1,
    },
}

/** Grouping for limit tables (plan form, drawer, settings). */
export const BILLING_PLAN_LIMIT_TYPES = ['Users', 'Jobs', 'Interviews', 'Content', 'Candidates'] as const
export type BillingPlanLimitType = (typeof BILLING_PLAN_LIMIT_TYPES)[number]

export type BillingPlanLimits = {
    maxUsers?: number | null
    maxActiveJobs?: number | null
    maxJobsTotal?: number | null
    maxScreeningInterviewsPerMonth?: number | null
    maxScreeningInterviewsTotal?: number | null
    maxHumanInterviewsPerMonth?: number | null
    maxJobDescriptions?: number | null
    maxAssessments?: number | null
    maxResumeParsesPerMonth?: number | null
}

export type BillingPlanLimitFieldKey = keyof BillingPlanLimits

export type BillingPlanLimitFieldMeta = {
    name: BillingPlanLimitFieldKey
    title: string
    description: string
    type: BillingPlanLimitType
}

/** Shared labels/descriptions for plan limit fields (forms, drawers, etc.). */
export const BILLING_PLAN_LIMIT_FIELDS = [
    {
        name: 'maxResumeParsesPerMonth',
        title: 'Max candidates / month',
        description: 'Legacy candidate allotment. Prefer credit wallet. Leave empty for unlimited.',
        type: 'Candidates',
    },
    {
        name: 'maxUsers',
        title: 'Max users',
        description: 'Maximum number of users allowed in the organization. Leave empty for unlimited.',
        type: 'Users',
    },
    {
        name: 'maxActiveJobs',
        title: 'Max active jobs',
        description: 'Non-deleted jobs with visibility other than Hidden. Leave empty for unlimited.',
        type: 'Jobs',
    },
    {
        name: 'maxJobsTotal',
        title: 'Max jobs (total)',
        description: 'Total non-deleted jobs for the organization. Leave empty for unlimited.',
        type: 'Jobs',
    },
    {
        name: 'maxScreeningInterviewsPerMonth',
        title: 'Max screening interviews / month',
        description: 'Legacy monthly AI screening quota. Prefer credits. Leave empty for unlimited.',
        type: 'Interviews',
    },
    {
        name: 'maxScreeningInterviewsTotal',
        title: 'Max screening interviews (total)',
        description: 'Legacy lifetime AI screening quota. Prefer credits. Leave empty for unlimited.',
        type: 'Interviews',
    },
    {
        name: 'maxHumanInterviewsPerMonth',
        title: 'Max human interviews / month',
        description: 'Legacy monthly human interview quota. Prefer credits. Leave empty for unlimited.',
        type: 'Interviews',
    },
    {
        name: 'maxJobDescriptions',
        title: 'Max job descriptions',
        description: 'Legacy JD document quota. Prefer credits. Leave empty for unlimited.',
        type: 'Content',
    },
    {
        name: 'maxAssessments',
        title: 'Max assessments',
        description: 'Maximum assessment templates. Leave empty for unlimited.',
        type: 'Content',
    },
] as const satisfies readonly BillingPlanLimitFieldMeta[]

export type BillingPlanLimitField = (typeof BILLING_PLAN_LIMIT_FIELDS)[number]

/** Fields grouped in `BILLING_PLAN_LIMIT_TYPES` order (empty groups omitted). */
export function groupBillingPlanLimitFieldsByType(): { type: BillingPlanLimitType; fields: BillingPlanLimitField[] }[] {
    return BILLING_PLAN_LIMIT_TYPES.map((type) => ({
        type,
        fields: BILLING_PLAN_LIMIT_FIELDS.filter((field) => field.type === type),
    })).filter((group) => group.fields.length > 0)
}

/** Empty limits object with every catalog key set to null (unlimited). */
export function emptyBillingPlanLimits(): Record<BillingPlanLimitFieldKey, null> {
    return Object.fromEntries(BILLING_PLAN_LIMIT_FIELDS.map((field) => [field.name, null])) as Record<BillingPlanLimitFieldKey, null>
}

/** Annual licensed amount from monthly base + discount percent. */
export function computeAnnualAmountCents(monthlyAmountCents: number, annualDiscountPercent: number): number {
    const discount = Math.min(100, Math.max(0, annualDiscountPercent))
    return Math.round(monthlyAmountCents * 12 * (1 - discount / 100))
}

export function computeCreditPackAmountCents(credits: number, unitAmountCents: number = CREDIT_UNIT_AMOUNT_CENTS): number {
    return credits * unitAmountCents
}

export function isBillingCreditPackSize(value: number): value is BillingCreditPackSize {
    return (BILLING_CREDIT_PACK_SIZES as readonly number[]).includes(value)
}

/** @deprecated Use isBillingCreditPackSize */
export const isBillingCapacityBlockSize = isBillingCreditPackSize

export function mergeBillingCreditCosts(
    defaults: BillingCreditCostsMap,
    overrides: BillingCreditCostsMap | null | undefined
): Record<BillingCreditActionCode, BillingCreditCostEntry> {
    const merged = { ...DEFAULT_BILLING_CREDIT_COSTS, ...defaults }
    if (overrides) {
        for (const code of BILLING_CREDIT_ACTION_CODES) {
            const entry = overrides[code]
            if (entry) merged[code] = { ...merged[code], ...entry }
        }
    }
    return merged as Record<BillingCreditActionCode, BillingCreditCostEntry>
}
